import React, { useMemo } from 'react';
import { Layout, Menu, MenuProps, Typography } from 'antd';
import {
    BulbOutlined,
    CameraOutlined,
    DatabaseOutlined,
    ExperimentOutlined,
    HomeOutlined,
    NodeIndexOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
    AI_HUB_SUBSYSTEMS,
    SUBSYSTEM_GROUP_LABELS,
    SUBSYSTEM_GROUP_ORDER,
} from './ai-hub-routes';

type MenuItems = NonNullable<MenuProps['items']>;

const { Sider, Content } = Layout;
const { Text } = Typography;

/**
 * Nested sidebar layout for /admin/ai. The outer AdminLayout's sidebar still
 * renders; this provides a sub-rail listing all 11 subsystems grouped by
 * their AI Admin Overhaul Q12 IA category.
 *
 * The right-hand outlet renders either the home page (relationship diagram)
 * or a per-subsystem placeholder (Phase 2). Phase 3 cutovers replaced the
 * placeholder with real Configure/Sandbox/Versions tabs.
 */
export const AIHubLayout: React.FC = () => {
    const location = useLocation();

    const items: MenuItems = useMemo(() => {
        const grouped: Record<string, typeof AI_HUB_SUBSYSTEMS> = {};
        for (const s of AI_HUB_SUBSYSTEMS) {
            grouped[s.group] = grouped[s.group] ?? [];
            grouped[s.group].push(s);
        }
        const out: MenuItems = [
            {
                key: '/admin/ai',
                icon: <HomeOutlined />,
                label: <Link to="/admin/ai">Hub Home</Link>,
            },
            {
                key: '/admin/ai/snapshots',
                icon: <CameraOutlined />,
                label: <Link to="/admin/ai/snapshots">Snapshots</Link>,
            },
            {
                key: '/admin/ai/knowledge_base/brands',
                icon: <DatabaseOutlined />,
                label: (
                    <Link to="/admin/ai/knowledge_base/brands">
                        Brand Knowledge
                    </Link>
                ),
            },
            {
                key: '/admin/ai/knowledge-distill',
                icon: <ExperimentOutlined />,
                label: (
                    <Link to="/admin/ai/knowledge-distill">Knowledge Distill</Link>
                ),
            },
            {
                key: '/admin/ai/field-suggestions',
                icon: <BulbOutlined />,
                label: (
                    <Link to="/admin/ai/field-suggestions">Field Suggestions</Link>
                ),
            },
            { type: 'divider' as const },
        ];
        for (const group of SUBSYSTEM_GROUP_ORDER) {
            const subsystems = grouped[group];
            if (!subsystems?.length) continue;
            out.push({
                type: 'group' as const,
                label: SUBSYSTEM_GROUP_LABELS[group],
                children: subsystems.map((s) => ({
                    key: `/admin/ai/${s.id}`,
                    label: <Link to={`/admin/ai/${s.id}`}>{s.displayName}</Link>,
                })),
            });
        }
        return out;
    }, []);

    const selectedKey = useMemo(() => {
        const path = location.pathname;
        if (path === '/admin/ai' || path === '/admin/ai/') return '/admin/ai';
        if (path.startsWith('/admin/ai/snapshots')) return '/admin/ai/snapshots';
        if (path.startsWith('/admin/ai/knowledge_base/brands'))
            return '/admin/ai/knowledge_base/brands';
        if (path.startsWith('/admin/ai/knowledge-distill'))
            return '/admin/ai/knowledge-distill';
        if (path.startsWith('/admin/ai/field-suggestions'))
            return '/admin/ai/field-suggestions';
        const exact = AI_HUB_SUBSYSTEMS.find((s) =>
            path.startsWith(`/admin/ai/${s.id}`),
        );
        return exact ? `/admin/ai/${exact.id}` : '/admin/ai';
    }, [location.pathname]);

    return (
        <Layout style={{ minHeight: 'calc(100vh - 200px)', background: 'transparent' }}>
            <Sider
                width={260}
                style={{
                    background: '#fff',
                    borderRight: '1px solid #f0f0f0',
                    paddingTop: 16,
                }}
            >
                <div style={{ padding: '0 24px 12px' }}>
                    <Text strong>
                        <NodeIndexOutlined style={{ marginRight: 8 }} />
                        AI Administration
                    </Text>
                </div>
                <Menu
                    mode="inline"
                    selectedKeys={[selectedKey]}
                    items={items}
                    style={{ borderRight: 0 }}
                />
            </Sider>
            <Content style={{ padding: 24, background: '#fff' }}>
                <Outlet />
            </Content>
        </Layout>
    );
};
